import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Send, ArrowLeft } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";

export default function Chat() {
  const { otherId } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [p, m] = await Promise.all([
        api.get(`/profile/${otherId}`),
        api.get(`/messages/${otherId}`),
      ]);
      setOther(p.data); setMessages(m.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/messages/${otherId}`);
        setMessages(data);
      } catch {}
    }, 4000);
    return () => clearInterval(t);
  }, [otherId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { to_user_id: otherId, text });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (!other) return <div className="max-w-4xl mx-auto px-6 py-32 text-center text-[#8C857B]">Loading…</div>;

  const cover = other.photos?.[0] ? fileUrl(other.photos[0]) : null;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-8 md:py-12" data-testid="chat-page">
      <Link to="/matches" className="text-sm text-[#8C857B] hover:text-[#C85A44] inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="h-4 w-4"/> {t("chat.back")}
      </Link>

      <div className="rounded-3xl border border-[#E2DCD0] bg-white overflow-hidden flex flex-col" style={{ height: "70vh" }}>
        <div className="flex items-center gap-4 px-6 py-4 border-b border-[#E2DCD0]">
          <Link to={`/profile/${other.id}`} className="relative w-12 h-12 rounded-full overflow-hidden bg-[#F2EFE9] flex-shrink-0">
            {cover ? <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" /> :
              <div className="absolute inset-0 flex items-center justify-center font-serif-display text-xl text-[#8C857B]">{other.full_name?.[0]}</div>}
          </Link>
          <div>
            <div className="font-serif-display text-xl leading-tight">{other.full_name}</div>
            <div className="text-xs text-[#8C857B]">{other.city || ""}</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-3" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-[#8C857B] text-sm">
              <p className="font-serif-display text-2xl text-[#2C2A28] mb-2">{t("chat.sayHello")}</p>
              <p>{t("chat.sayHelloBody")}</p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.from_user_id === user.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`msg-${m.id}`}
                    className={`max-w-[75%] px-4 py-2.5 text-sm leading-relaxed ${
                      mine ? "bg-[#C85A44] text-white rounded-2xl rounded-tr-sm" : "bg-[#F2EFE9] text-[#2C2A28] rounded-2xl rounded-tl-sm"
                    }`}>
                    {m.text}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <form onSubmit={send} className="border-t border-[#E2DCD0] p-3 flex items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} data-testid="chat-input"
            placeholder={t("chat.placeholder")}
            className="flex-1 rounded-full bg-[#F9F8F6] border border-[#E2DCD0] px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30" />
          <button type="submit" disabled={sending || !text.trim()} data-testid="chat-send"
            className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] disabled:opacity-50 text-white p-3 transition">
            <Send className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </form>
      </div>
    </div>
  );
}
